import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Edit, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const EMPTY = { audience: "professional", category: "", name: "", description: "", price_display: "", price_amount: null, currency: "usd", inclusions: [], is_draft: true, is_active: true, checkout_enabled: false, featured: false, order: 50 };

export default function AdminPricing() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [dollars, setDollars] = useState("");
  const [incText, setIncText] = useState("");

  const load = useCallback(() => api.get("/admin/packages").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setForm(EMPTY); setDollars(""); setIncText(""); setEditing("new"); };
  const openEdit = (p) => {
    setForm({ ...p });
    setDollars(p.price_amount ? (p.price_amount / 100).toString() : "");
    setIncText((p.inclusions || []).join("\n"));
    setEditing(p.id);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price_amount: dollars ? Math.round(parseFloat(dollars) * 100) : null,
      inclusions: incText.split("\n").map((x) => x.trim()).filter(Boolean),
    };
    try {
      if (editing === "new") await api.post("/admin/packages", payload);
      else await api.put(`/admin/packages/${editing}`, payload);
      toast.success("Saved");
      setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this package?")) return;
    await api.delete(`/admin/packages/${id}`);
    toast.success("Deleted"); load();
  };

  const inputCls = "w-full bg-ivory border border-champagne/40 rounded-lg px-4 py-3 text-espresso focus:outline-none focus:ring-2 focus:ring-champagne";
  const groups = { business: items.filter((i) => i.audience === "business"), professional: items.filter((i) => i.audience === "professional") };

  return (
    <div data-testid="admin-pricing">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-4xl text-espresso">Pricing</h1>
          <p className="text-espresso/60 mt-2">Edit packages, prices, inclusions and availability. Prices update the live site instantly.</p>
        </div>
        <button onClick={openNew} data-testid="pricing-new" className="inline-flex items-center gap-2 bg-espresso text-ivory rounded-full px-5 py-2.5 text-sm font-medium hover:bg-forest transition-colors">
          <Plus size={16} /> New Package
        </button>
      </div>

      {["professional", "business"].map((aud) => (
        <div key={aud} className="mt-8">
          <h2 className="font-serif text-2xl text-espresso capitalize mb-4">For {aud === "business" ? "Businesses" : "Professionals"}</h2>
          <div className="space-y-3">
            {groups[aud].map((p) => (
              <div key={p.id} className="bg-taupe rounded-2xl p-5 flex items-center justify-between gap-4" data-testid={`pricing-row-${p.id}`}>
                <div className="min-w-0">
                  <h3 className="font-serif text-lg text-espresso">{p.name} <span className="text-espresso/50 text-sm font-sans">· {p.category}</span></h3>
                  <p className="text-espresso/60 text-sm">
                    {p.checkout_enabled && p.price_amount ? `$${(p.price_amount / 100).toFixed(2)} · online checkout` : p.price_display || "No price"}
                    {p.is_draft && " · draft"} {p.featured && " · featured"} {!p.is_active && " · hidden"}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => openEdit(p)} data-testid={`pricing-edit-${p.id}`} className="p-2 rounded-lg bg-ivory hover:bg-champagne transition-colors"><Edit size={16} className="text-espresso" /></button>
                  <button onClick={() => remove(p.id)} data-testid={`pricing-delete-${p.id}`} className="p-2 rounded-lg bg-ivory hover:bg-red-200 transition-colors"><Trash2 size={16} className="text-espresso" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {editing && (
        <div className="fixed inset-0 bg-espresso/60 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} data-testid="pricing-form" className="bg-ivory rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-espresso">{editing === "new" ? "New Package" : "Edit Package"}</h2>
              <button type="button" onClick={() => setEditing(null)}><X className="text-espresso" /></button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <select value={form.audience} onChange={(e) => set("audience", e.target.value)} className={inputCls} data-testid="pricing-audience">
                  <option value="professional">For Professionals</option>
                  <option value="business">For Businesses</option>
                </select>
                <input value={form.category} onChange={(e) => set("category", e.target.value)} placeholder="Category" className={inputCls} data-testid="pricing-category" />
              </div>
              <input required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Package name" className={inputCls} data-testid="pricing-name" />
              <textarea value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Description" rows={2} className={inputCls} data-testid="pricing-description" />
              <div className="grid grid-cols-2 gap-4">
                <input value={dollars} onChange={(e) => setDollars(e.target.value)} placeholder="Price in $ (e.g. 199)" type="number" step="0.01" className={inputCls} data-testid="pricing-amount" />
                <input value={form.price_display} onChange={(e) => set("price_display", e.target.value)} placeholder='Display label (e.g. "Custom Quote")' className={inputCls} data-testid="pricing-display" />
              </div>
              <textarea value={incText} onChange={(e) => setIncText(e.target.value)} placeholder="Inclusions (one per line)" rows={4} className={inputCls} data-testid="pricing-inclusions" />
              <div className="flex flex-wrap gap-5">
                <label className="flex items-center gap-2 text-espresso text-sm"><input type="checkbox" checked={form.checkout_enabled} onChange={(e) => set("checkout_enabled", e.target.checked)} data-testid="pricing-checkout" /> Enable online checkout</label>
                <label className="flex items-center gap-2 text-espresso text-sm"><input type="checkbox" checked={form.is_draft} onChange={(e) => set("is_draft", e.target.checked)} /> Draft price</label>
                <label className="flex items-center gap-2 text-espresso text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} /> Featured</label>
                <label className="flex items-center gap-2 text-espresso text-sm"><input type="checkbox" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} /> Visible on site</label>
              </div>
              <p className="text-xs text-espresso/50">Online checkout requires a price in $. Business packages are typically left as "Custom Quote" without checkout.</p>
              <button type="submit" data-testid="pricing-save" className="bg-espresso text-ivory rounded-full px-7 py-3 font-medium hover:bg-forest transition-colors">Save Package</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
